import { format, parseISO, isValid, differenceInCalendarDays } from "date-fns";
import { CheckSquare } from "react-bootstrap-icons";
import { projectionIdentities, formatMoney } from "../../calendar/common";

// Task types the calendar stores on a missed-payment task (from the intelligence schedule).
export const TASK_TYPES = {
  installmentMissed: "InstallmentMissed",
  promiseMissed: "PromiseMissed",
  // A merchant-scheduled chase that went unpaid — distinct from a promise so the copy never claims the
  // customer broke a commitment they never made, and carries its own schedule visual identity.
  scheduleMissed: "ScheduleMissed",
  incomingPaymentReview: "IncomingPaymentReview",
};

const isInstalment = (task) =>
  task.taskType === TASK_TYPES.installmentMissed ||
  (task.instalmentCount != null && task.instalmentCount > 1);

const hasInstalmentNumbers = (task) =>
  task.instalmentNumber != null && task.instalmentCount != null;

// An IncomingPaymentReview gates a send, so it reads as the send it is: a merchant-scheduled chase
// takes the scheduled-payment-request identity (send-clock, payment-request orange), everything else
// the promise identity (teal). Mirrors the calendar's projectionIdentity discriminator.
const reviewIdentity = (task) =>
  task.scheduleKind === "schedule"
    ? projectionIdentities.ScheduledPaymentRequest
    : projectionIdentities.PromiseToPay;

// The action label shown in the chip on the card.
export const taskTag = (task) => {
  if (isInstalment(task)) return "Chase overdue instalment";
  if (task.taskType === TASK_TYPES.promiseMissed) return "Follow up on lapsed promise";
  if (task.taskType === TASK_TYPES.scheduleMissed) return "Follow up on scheduled request";
  if (task.taskType === TASK_TYPES.incomingPaymentReview)
    return task.scheduleKind === "schedule" ? "Scheduled request — possibly paid" : "Promise — possibly paid";
  // Everything else is a plain user-created task (e.g. from the AI chat) — no payment context.
  return "Scheduled task";
};

// The card chip is a static label, not an interactive grid chip — drop the hover:* classes.
const staticChip = (cls) => cls.split(" ").filter((c) => !c.startsWith("hover:")).join(" ");

// The tag chip's colour, aligned with the calendar identities so a task reads the same colour on the
// card as it does on the calendar: payment plans indigo, promises to pay the promise teal, a
// merchant-scheduled chase the schedule fuchsia, plain tasks the neutral slate tasks carry everywhere.
export const taskTagStyle = (task) => {
  if (isInstalment(task)) return staticChip(projectionIdentities.PaymentPlan.chip);
  if (task.taskType === TASK_TYPES.scheduleMissed) {
    return staticChip(projectionIdentities.ScheduledPaymentRequest.chip);
  }
  if (task.taskType === TASK_TYPES.promiseMissed) return staticChip(projectionIdentities.PromiseToPay.chip);
  if (task.taskType === TASK_TYPES.incomingPaymentReview) return staticChip(reviewIdentity(task).chip);
  return "bg-slate-100 text-slate-700";
};

// The chip's leading icon, matching the calendar identities: payment plans the send-clock, promises
// to pay the handshake, a merchant-scheduled chase the scheduled-payment glyph, plain tasks the
// checkbox they carry across the calendar and feed.
export const taskTagIcon = (task) => {
  if (isInstalment(task)) return projectionIdentities.PaymentPlan.Icon;
  if (task.taskType === TASK_TYPES.scheduleMissed) return projectionIdentities.ScheduledPaymentRequest.Icon;
  if (task.taskType === TASK_TYPES.promiseMissed) return projectionIdentities.PromiseToPay.Icon;
  if (task.taskType === TASK_TYPES.incomingPaymentReview) return reviewIdentity(task).Icon;
  return CheckSquare;
};

// "was due yesterday" / "is due today" / "was due on 12 Aug", computed from the promised date so the
// wording stays correct as days pass.
const duePhrase = (promisedDate) => {
  if (!promisedDate) return null;
  const parsed = parseISO(promisedDate);
  if (!isValid(parsed)) return null;

  const days = differenceInCalendarDays(new Date(), parsed);
  if (days === 0) return "is due today";
  if (days === 1) return "was due yesterday";
  if (days > 1) return `was due on ${format(parsed, "d MMM")}`;
  return `is due on ${format(parsed, "d MMM")}`;
};

// The pre-send check findings phrased for the card, when the BFF surfaced them. Null when there was
// no amount to show, so the description falls back to the generic "possibly paid" copy.
//   - several payments: a source-neutral running total, since we can't attribute each one
//   - one payment: who saw it — a payment we took ("ontik") vs one we only saw on the ledger
const detectedPhrase = (task) => {
  if (!(task.detectedAmount > 0)) return null;
  const amount = formatMoney(task.detectedAmount);
  const when = task.detectedAt ? parseISO(task.detectedAt) : null;
  const date = when && isValid(when) ? format(when, "d MMM") : null;

  if (task.detectedCount > 1) {
    const latest = date ? ` (latest ${date})` : "";
    return `a total of ${amount} has arrived since this payment was scheduled${latest}`;
  }

  const on = date ? ` on ${date}` : "";
  return task.detectedSource === "ledger"
    ? `the ledger shows ${amount} paid${on}`
    : `${amount} was paid${on}`;
};

// A one-line description composed from the task's facts (not a baked sentence), so the wording stays
// correct as days pass. Cadence-neutral: we know the instalment count, not the plan's frequency.
export const taskDescription = (task) => {
  const due = duePhrase(task.promisedDate);

  if (task.taskType === TASK_TYPES.incomingPaymentReview) {
    const amount = formatMoney(task.promisedAmount);
    const isSchedule = task.scheduleKind === "schedule";
    const subject = isSchedule
      ? amount ? `Scheduled payment request for ${amount}` : "Scheduled payment request"
      : amount ? `Promised payment of ${amount}` : "Promised payment";

    const seen = detectedPhrase(task);
    if (seen) {
      return due
        ? `${subject} ${due} — but ${seen}. Review before chasing.`
        : `${subject} — but ${seen}. Review before chasing.`;
    }

    // A promise reads naturally as "payment may have arrived"; a scheduled request is a send, so spell
    // out the arriving payment rather than claiming the request itself arrived.
    if (isSchedule) {
      return `${due ? `${subject} ${due}` : subject} — a payment may already have arrived. Review before sending the request.`;
    }

    return due
      ? `${subject} ${due} — a payment may already have arrived. Review before sending the request.`
      : `${subject} may already have arrived. Review before sending the request.`;
  }

  if (isInstalment(task) && hasInstalmentNumbers(task)) {
    const plan = `On a ${task.instalmentCount}-instalment plan, instalment ${task.instalmentNumber} of ${task.instalmentCount}`;
    return due ? `${plan} ${due} and hasn't arrived.` : `${plan} hasn't arrived.`;
  }

  if (task.taskType === TASK_TYPES.scheduleMissed) {
    return due
      ? `Scheduled payment request ${due} and hasn't been paid.`
      : "Scheduled payment request hasn't been paid.";
  }

  return due
    ? `Promised payment ${due} and hasn't arrived.`
    : "Promised payment hasn't arrived.";
};
